import * as React from "react";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { WORKFORCE_PERSONAS } from "../../../../../convex/aiWorkforce/config";
import { getPersonaTheme } from "./personaTheme";
import { WorkDeliverableCard } from "./WorkDeliverableCard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Briefcase,
  Search,
  CheckCircle2,
  Clock,
  FileText,
  Bot,
  ExternalLink,
  Filter,
  XCircle,
  AlertCircle,
} from "lucide-react";

interface AllWorkModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId?: string;
  personalWorkspaceId?: string;
  onSelectArtifact: (artifactId: string, threadId: string) => void;
}

export function AllWorkModal({
  isOpen,
  onClose,
  orgId,
  personalWorkspaceId,
  onSelectArtifact,
}: AllWorkModalProps) {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<string>("all");
  const [personaFilter, setPersonaFilter] = React.useState<string>("all");

  const allArtifacts = useQuery(
    api.aiWorkforce.artifacts.listAllArtifacts,
    isOpen ? { orgId: orgId as any, personalWorkspaceId: personalWorkspaceId as any } : "skip"
  );

  const formatArtifactType = (type: string) => {
    switch (type) {
      case "lead_batch_preview":
        return "Lead Batch";
      case "work_order_draft":
        return "Work Order";
      case "invoice_preview":
        return "Invoice Draft";
      case "shift_grid":
        return "Shift Schedule";
      case "hr_onboarding_packet":
        return "Onboarding Packet";
      case "client_message_draft":
        return "Client Message";
      case "composio_action_preview":
        return "Tool Action";
      default:
        return type.replace(/_/g, " ");
    }
  };

  const filteredArtifacts = React.useMemo(() => {
    if (!allArtifacts) return [];
    return allArtifacts.filter((item: any) => {
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      if (personaFilter !== "all" && item.createdPersona !== personaFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = item.title?.toLowerCase().includes(q);
        const matchPersona = item.createdPersona?.toLowerCase().includes(q);
        const matchType = item.type?.toLowerCase().includes(q);
        if (!matchTitle && !matchPersona && !matchType) return false;
      }
      return true;
    });
  }, [allArtifacts, statusFilter, personaFilter, searchQuery]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden bg-white">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b border-gray-100 flex-shrink-0 bg-slate-50/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center shadow-xs">
                <Briefcase className="w-5 h-5" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold text-gray-900">
                  All Workforce Work & Deliverables
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500 mt-0.5">
                  Aggregated work items, draft documents, shift grids, and approvals across all agents.
                </DialogDescription>
              </div>
            </div>
          </div>

          {/* Controls: Search and Filters */}
          <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search deliverables by title, agent, or type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 text-sm rounded-lg bg-white"
              />
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-9 px-3 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="draft">Draft</option>
                <option value="rejected">Rejected</option>
              </select>

              <select
                value={personaFilter}
                onChange={(e) => setPersonaFilter(e.target.value)}
                className="h-9 px-3 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">All Agents</option>
                {Object.values(WORKFORCE_PERSONAS).map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.name} ({p.title.replace(" Agent", "")})
                  </option>
                ))}
              </select>
            </div>
          </div>
        </DialogHeader>

        {/* Deliverables List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3 custom-scrollbar min-h-[300px]">
          {allArtifacts === undefined ? (
            <div className="py-12 flex flex-col items-center justify-center text-gray-400">
              <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm font-medium">Loading workforce deliverables...</p>
            </div>
          ) : filteredArtifacts.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center text-center">
              <div className="w-12 h-12 rounded-2xl bg-gray-100 text-gray-400 flex items-center justify-center mb-3">
                <FileText className="w-6 h-6" />
              </div>
              <h3 className="text-base font-semibold text-gray-800">No deliverables found</h3>
              <p className="text-sm text-gray-500 max-w-sm mt-1">
                {searchQuery || statusFilter !== "all" || personaFilter !== "all"
                  ? "No deliverables match your search and filter criteria."
                  : "Work outputs generated by your agents (proposals, schedules, invoices, and drafts) will appear here."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2.5">
              {filteredArtifacts.map((artifact: any) => (
                <WorkDeliverableCard
                  key={artifact._id}
                  artifact={artifact}
                  onOpen={(artifactId, threadId) => {
                    onSelectArtifact(artifactId, threadId);
                    onClose();
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
          <span>
            Showing {filteredArtifacts.length} {filteredArtifacts.length === 1 ? "deliverable" : "deliverables"}
          </span>
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
